import { RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/welcome" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl text-gray-900">BannerSync</span>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-28 pb-20 px-6">
        <div className="max-w-4xl mx-auto prose prose-gray prose-lg">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-gray-500 mb-8">Last updated: February 2025</p>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">1. Introduction</h2>
            <p className="text-gray-600 leading-relaxed">
              BannerSync is a trading name of Nineteen Twelve Management Ltd (Company No. 12345678), 
              registered in England and Wales. We are committed to protecting your privacy and handling 
              your personal data in accordance with the UK General Data Protection Regulation (UK GDPR) 
              and the Data Protection Act 2018.
            </p>
            <p className="text-gray-600 leading-relaxed mt-4">
              This Privacy Policy explains how we collect, use, store, and protect your personal information 
              when you use our banner management platform and related services.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">2. Data Controller</h2>
            <p className="text-gray-600 leading-relaxed">
              Nineteen Twelve Management Ltd is the data controller responsible for your personal data. 
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <ul className="list-none pl-0 mt-4 text-gray-600">
              <li><strong>Email:</strong> hello@bannersync.co.uk</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">3. Information We Collect</h2>
            
            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">3.1 Account Information</h3>
            <p className="text-gray-600 leading-relaxed">
              When you register for a BannerSync account, we collect:
            </p>
            <ul className="list-disc pl-6 mt-2 text-gray-600 space-y-1">
              <li>Email address</li>
              <li>Display name (optional)</li>
              <li>Password (stored securely using industry-standard hashing)</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">3.2 Analytics Data</h3>
            <p className="text-gray-600 leading-relaxed">
              Our banner analytics system collects privacy-respecting data about banner views and clicks:
            </p>
            <ul className="list-disc pl-6 mt-2 text-gray-600 space-y-1">
              <li>Anonymised IP address (truncated and hashed with a daily rotating salt)</li>
              <li>Browser family and operating system (e.g., "Chrome", "Windows")</li>
              <li>Device type (desktop, mobile, tablet)</li>
              <li>Referring domain (the website where the banner was displayed)</li>
              <li>Timestamp of the interaction</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              <strong>We do not use cookies for analytics tracking.</strong> We do not store complete IP 
              addresses or any persistent identifiers that could be used to track individuals across sessions.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">3.3 Contact Form Submissions</h3>
            <p className="text-gray-600 leading-relaxed">
              When you submit our contact form, we collect:
            </p>
            <ul className="list-disc pl-6 mt-2 text-gray-600 space-y-1">
              <li>Name</li>
              <li>Email address</li>
              <li>Company name (optional)</li>
              <li>Message content</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">4. How We Use Your Information</h2>
            <p className="text-gray-600 leading-relaxed">We use your personal data for the following purposes:</p>
            <ul className="list-disc pl-6 mt-2 text-gray-600 space-y-2">
              <li><strong>Account Management:</strong> To create and manage your account, authenticate your access, and provide customer support.</li>
              <li><strong>Service Delivery:</strong> To provide our banner management and analytics services.</li>
              <li><strong>Communication:</strong> To respond to your enquiries and send important service updates.</li>
              <li><strong>Analytics:</strong> To provide you with aggregated statistics about your banner performance.</li>
              <li><strong>Security:</strong> To detect and prevent fraud, abuse, and security incidents.</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">5. Legal Basis for Processing</h2>
            <p className="text-gray-600 leading-relaxed">We process your personal data under the following legal bases:</p>
            <ul className="list-disc pl-6 mt-2 text-gray-600 space-y-2">
              <li><strong>Contract:</strong> Processing necessary to fulfil our contract with you (providing our services).</li>
              <li><strong>Legitimate Interests:</strong> Processing for our legitimate business interests, such as improving our services and ensuring security.</li>
              <li><strong>Consent:</strong> Where you have given explicit consent for specific processing activities.</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">6. Data Retention</h2>
            <ul className="list-disc pl-6 mt-2 text-gray-600 space-y-2">
              <li><strong>Account Data:</strong> Retained for as long as your account is active, plus a reasonable period for backup and legal compliance.</li>
              <li><strong>Raw Analytics Events:</strong> Automatically deleted after 90 days.</li>
              <li><strong>Aggregated Analytics:</strong> Retained indefinitely in anonymised, aggregated form.</li>
              <li><strong>Contact Form Submissions:</strong> Retained for 2 years or until your enquiry is resolved.</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">7. Data Sharing</h2>
            <p className="text-gray-600 leading-relaxed">
              We do not sell your personal data. We may share your data with:
            </p>
            <ul className="list-disc pl-6 mt-2 text-gray-600 space-y-2">
              <li><strong>Service Providers:</strong> Third-party services that help us operate our platform (e.g., email delivery, hosting). These providers are contractually bound to protect your data.</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our legal rights.</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">8. Your Rights</h2>
            <p className="text-gray-600 leading-relaxed">Under UK GDPR, you have the following rights:</p>
            <ul className="list-disc pl-6 mt-2 text-gray-600 space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal data.</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate data.</li>
              <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten").</li>
              <li><strong>Portability:</strong> Request your data in a machine-readable format.</li>
              <li><strong>Objection:</strong> Object to processing based on legitimate interests.</li>
              <li><strong>Restriction:</strong> Request restricted processing in certain circumstances.</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              To exercise any of these rights, please contact us at hello@bannersync.co.uk.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">9. Security</h2>
            <p className="text-gray-600 leading-relaxed">
              We implement appropriate technical and organisational measures to protect your personal data, including:
            </p>
            <ul className="list-disc pl-6 mt-2 text-gray-600 space-y-1">
              <li>Encryption of data in transit (HTTPS/TLS)</li>
              <li>Secure password hashing using industry-standard algorithms</li>
              <li>Regular security reviews and updates</li>
              <li>Access controls and authentication measures</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">10. Cookies</h2>
            <p className="text-gray-600 leading-relaxed">
              BannerSync uses minimal cookies strictly necessary for the operation of our service:
            </p>
            <ul className="list-disc pl-6 mt-2 text-gray-600 space-y-1">
              <li><strong>Authentication:</strong> Session cookies to keep you logged in.</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              We do not use third-party tracking cookies or advertising cookies.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">11. Changes to This Policy</h2>
            <p className="text-gray-600 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any material changes 
              by posting the new policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">12. Complaints</h2>
            <p className="text-gray-600 leading-relaxed">
              If you have concerns about how we handle your personal data, please contact us first at 
              hello@bannersync.co.uk. You also have the right to lodge a complaint with the Information 
              Commissioner's Office (ICO) at <a href="https://ico.org.uk" className="text-emerald-600 hover:underline">ico.org.uk</a>.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/welcome" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl">BannerSync</span>
            </Link>
            
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} BannerSync. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
